import React, { useState, useEffect } from "react";
import AudiogramChart from "./AudiogramChart";
import { FREQUENCIES } from "./constants";

// dB và volume
const STEP_DB = 5;
const MIN_DB = -10;
const MAX_DB = 120;
const DB_LEVELS = Array.from({ length: Math.floor((MAX_DB - MIN_DB) / STEP_DB) + 1 }, (_, i) => MIN_DB + i * STEP_DB);

// Giá trị tinh chỉnh min/max/step
const VOL_MIN = 0.00001;
const VOL_MAX = 1;
const VOL_STEP = 0.00001;

function createDefaultCalibration() {
  // { [freq]: [{ db, volume }] }
  const calib = {};
  for (const freq of FREQUENCIES) {
    calib[freq] = DB_LEVELS.map((db, idx) => ({
      db, volume: Math.max(VOL_MIN, Math.round((db + 10) * 1.0e4 / (MAX_DB + 10)) / 1.0e4) // mặc định tuyến tính, min 0.0001
    }));
  }
  return calib;
}

function CalibrationTable({ calib, onChange, onPlay, onSave, onClose }) {
  return (
    <div style={{
      position: "fixed", top: 40, left: "5%", width: "90%",
      background: "#fff", border: "2px solid #1976d2", borderRadius: 12,
      zIndex: 9999, boxShadow: "0 2px 16px #1976d2", padding: 24, maxHeight: "90vh", overflowY: "auto"
    }}>
      <h2 style={{ color: "#1976d2", textAlign: "center" }}>BẢNG CHUẨN ÂM LƯỢNG (volume) cho từng mức dB</h2>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr>
            <th>Tần số (Hz)</th>
            {DB_LEVELS.map(db => <th key={db}>{db} dB</th>)}
          </tr>
        </thead>
        <tbody>
          {FREQUENCIES.map((freq) => (
            <tr key={freq}>
              <td style={{ fontWeight: "bold", border: "1px solid #ccc", padding: 4 }}>{freq}</td>
              {calib[freq].map((item, dbIdx) => (
                <td key={dbIdx} style={{ border: "1px solid #ccc", padding: 2, textAlign: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <button
                      onClick={() => onChange(freq, dbIdx, Math.max(item.volume - VOL_STEP, VOL_MIN))}
                      style={{ fontSize: 12 }}
                      title="Giảm"
                    >–</button>
                    <input
                      type="number"
                      min={VOL_MIN}
                      max={VOL_MAX}
                      step={VOL_STEP}
                      value={item.volume}
                      style={{ width: 80, textAlign: "right", margin: "0 4px" }}
                      onChange={e => onChange(freq, dbIdx, Math.max(VOL_MIN, Math.min(VOL_MAX, Number(e.target.value))))}
                    />
                    <button
                      onClick={() => onChange(freq, dbIdx, Math.min(item.volume + VOL_STEP, VOL_MAX))}
                      style={{ fontSize: 12 }}
                      title="Tăng"
                    >+</button>
                  </div>
                  <span style={{ fontSize: 10, color: "#888" }}>({item.volume})</span>
                  <button style={{ fontSize: 11, marginTop: 2 }}
                    onClick={() => onPlay(freq, dbIdx, item.volume)}
                  >Phát thử</button>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ textAlign: "center", margin: "12px 0" }}>
        <button onClick={onSave} style={{ fontSize: 18, padding: "8px 36px", background: "#1976d2", color: "#fff", borderRadius: 8 }}>
          Lưu & Áp dụng
        </button>
        <button onClick={onClose} style={{ fontSize: 18, padding: "8px 36px", marginLeft: 8, background: "#bbb", color: "#fff", borderRadius: 8 }}>
          Đóng bảng chuẩn (Ctrl+Shift+S)
        </button>
      </div>
      <div style={{fontSize:12, marginTop:8, color:"#888"}}>
        <b>Lưu ý:</b> Âm lượng mỗi ô được chỉnh từ {VOL_MIN} đến {VOL_MAX} (có thể nhập hoặc dùng nút tăng/giảm, step {VOL_STEP}). Giá trị này sẽ được dùng để phát âm thanh cho từng mức dB.
      </div>
    </div>
  );
}

function App() {
  // Danh sách bệnh nhân mẫu
  const [patients, setPatients] = useState([
    { id: "p1", name: "Nguyễn Văn A", dob: "1990-01-01", info: "Nam" },
    { id: "p2", name: "Trần Thị B", dob: "1992-02-02", info: "Nữ" },
    { id: "p3", name: "Lê Văn C", dob: "1995-03-03", info: "Nam" },
  ]);
  const [searchText, setSearchText] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(patients[0]);

  // Kết quả đo từng bệnh nhân
  const [resultsByPatient, setResultsByPatient] = useState(() => {
    const saved = localStorage.getItem("resultsByPatient");
    return saved ? JSON.parse(saved) : {};
  });

  // State đo thính lực
  const [currentFreqIdx, setCurrentFreqIdx] = useState(2);
  const [currentDbIdx, setCurrentDbIdx] = useState(DB_LEVELS.findIndex(db => db === 40));
  const [currentEar, setCurrentEar] = useState("right");
  const [conduction, setConduction] = useState("air");
  const [masking, setMasking] = useState(false);
  const [soundType, setSoundType] = useState("continuous");

  // Bảng chuẩn
  const [calib, setCalib] = useState(() => {
    const saved = localStorage.getItem("calibration-volume-table-extreme");
    return saved ? JSON.parse(saved) : createDefaultCalibration();
  });
  const [appliedCalib, setAppliedCalib] = useState(calib);
  const [showCalib, setShowCalib] = useState(false);

  useEffect(() => {
    localStorage.setItem("calibration-volume-table-extreme", JSON.stringify(calib));
  }, [calib]);
  useEffect(() => {
    localStorage.setItem("resultsByPatient", JSON.stringify(resultsByPatient));
  }, [resultsByPatient]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.ctrlKey && e.shiftKey && (e.key === "S" || e.key === "s")) {
        e.preventDefault(); setShowCalib(show => !show); return;
      }
      if (!selectedPatient) return;
      if (["ArrowUp", "ArrowDown", "ArrowRight", "ArrowLeft"].includes(e.key)) e.preventDefault();
      if (e.key === "ArrowUp") setCurrentDbIdx(idx => Math.max(idx - 1, 0));
      if (e.key === "ArrowDown") setCurrentDbIdx(idx => Math.min(idx + 1, DB_LEVELS.length - 1));
      if (e.key === "ArrowRight") setCurrentFreqIdx(idx => Math.min(idx + 1, FREQUENCIES.length - 1));
      if (e.key === "ArrowLeft") setCurrentFreqIdx(idx => Math.max(idx - 1, 0));
      if (e.key === "Enter") handlePlayTone();
      if (e.ctrlKey && e.code === "Space") { e.preventDefault(); handleSaveResult(); }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [selectedPatient, currentFreqIdx, currentDbIdx, currentEar, conduction, masking, soundType, showCalib, appliedCalib]);

  // Chỉnh bảng chuẩn
  const handleCalibChange = (freq, dbIdx, volume) => {
    setCalib(prev => {
      const next = { ...prev };
      next[freq] = prev[freq].map((item, i) =>
        i === dbIdx ? { ...item, volume } : item
      );
      return next;
    });
  };

  // Phát thử âm lượng từng nấc
  const handlePlayCalib = (freq, dbIdx, volume) => {
    playTone(freq, volume, "continuous");
  };

  // Lưu & áp dụng calibration đang chỉnh
  const handleSaveCalib = () => {
    setAppliedCalib(calib);
    alert("Đã lưu và áp dụng bảng chuẩn!");
  };

  // Phát âm khi đo, dùng bảng đã lưu & áp dụng
  const handlePlayTone = () => {
    const freq = FREQUENCIES[currentFreqIdx];
    const dbIdx = currentDbIdx;
    const volume = appliedCalib[freq][dbIdx].volume;
    playTone(freq, volume, soundType);
  };

  // Lưu kết quả đo cho từng bệnh nhân
  const handleSaveResult = () => {
    if (!selectedPatient) return;
    const freq = FREQUENCIES[currentFreqIdx];
    const dbIdx = currentDbIdx;
    const volume = appliedCalib[freq][dbIdx].volume;
    setResultsByPatient(prev => {
      const pid = selectedPatient.id;
      const arr = prev[pid] || [];
      const keyMatch = (r) => r.freq === freq && r.ear === currentEar && r.conduction === conduction;
      const idx = arr.findIndex(keyMatch);
      if (idx !== -1) {
        arr[idx] = { freq, db: DB_LEVELS[dbIdx], volume, ear: currentEar, conduction, masking };
        return { ...prev, [pid]: [...arr] };
      } else {
        return { ...prev, [pid]: [...arr, { freq, db: DB_LEVELS[dbIdx], volume, ear: currentEar, conduction, masking }] };
      }
    });
  };

  const handleClearResults = () => {
    if (!selectedPatient) return;
    setResultsByPatient(prev => ({ ...prev, [selectedPatient.id]: [] }));
  };

  const handleSelectPatient = (p) => setSelectedPatient(p);

  const filteredPatients = patients.filter(p =>
    p.name?.toLowerCase().includes(searchText.toLowerCase()) ||
    p.id?.toString().includes(searchText)
  );
  const results = selectedPatient ? (resultsByPatient[selectedPatient.id] || []) : [];

  // Phát âm thanh: mô phỏng set volume hệ thống bằng gain
  function playTone(freq, volume, type = "continuous", duration = 2000) {
    // volume: 0.00001–1, chuyển về gain (0–1, hoặc dùng thẳng để mô phỏng)
    const gainVal = Math.max(VOL_MIN, Math.min(1, volume));
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    g.gain.value = gainVal;
    osc.connect(g);
    g.connect(ctx.destination);
    if (type === "continuous") {
      osc.start();
      setTimeout(() => { osc.stop(); ctx.close(); }, duration);
    } else if (type === "pulsed") {
      let onTime = 200, offTime = 200, repeat = Math.floor(duration / (onTime + offTime));
      let count = 0;
      osc.start();
      function pulse() {
        if (count >= repeat) {
          osc.stop(); ctx.close(); return;
        }
        g.gain.value = gainVal;
        setTimeout(() => {
          g.gain.value = 0;
          setTimeout(() => {
            count++;
            g.gain.value = gainVal;
            pulse();
          }, offTime);
        }, onTime);
      }
      pulse();
    }
  }

  return (
    <div style={{ background: "#f5f7fa", minHeight: "100vh", padding: 24 }}>
      <h1 style={{ color: "#1976d2", textAlign: "center" }}>Phần mềm đo thính lực sơ bộ - SMIPM</h1>
      {/* BẢNG CHUẨN ÂM THANH */}
      {showCalib &&
        <CalibrationTable
          calib={calib}
          onChange={handleCalibChange}
          onPlay={handlePlayCalib}
          onSave={handleSaveCalib}
          onClose={() => setShowCalib(false)}
        />
      }
      {/* Thông tin bệnh nhân và tìm kiếm */}
      <div style={{ background: "#fff", padding: 16, borderRadius: 8, marginBottom: 24, boxShadow: "0 1px 4px #ccc" }}>
        <div style={{ marginBottom: 8 }}>
          <input
            type="text"
            placeholder="Tìm kiếm bệnh nhân"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 200, marginRight: 12 }}
          />
        </div>
        <div style={{ height: 120, overflowY: "auto", border: "1px solid #ddd", borderRadius: 4, marginBottom: 8 }}>
          {filteredPatients.map(p => (
            <div
              key={p.id}
              style={{
                padding: 6,
                background: selectedPatient?.id === p.id ? "#e3f2fd" : "#fff",
                cursor: "pointer",
                fontWeight: selectedPatient?.id === p.id ? "bold" : "normal"
              }}
              onClick={() => handleSelectPatient(p)}
            >
              {p.id}: {p.name} ({p.dob}) {p.info && `- ${p.info}`}
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button>Thêm</button>
          <button>Sửa</button>
          <button>Xóa</button>
        </div>
      </div>
      {/* Trình điều khiển */}
      <div style={{ background: "#fff", padding: 16, borderRadius: 8, marginBottom: 24, boxShadow: "0 1px 4px #ccc" }}>
        <div style={{ fontSize: 22, fontWeight: "bold", color: "#1976d2", marginBottom: 16 }}>Trình điều khiển</div>
        <div style={{ display: "flex", alignItems: "center", gap: 32 }}>
          <div>
            <div>Cường độ âm (dB): <span style={{ fontWeight: "bold", fontSize: 20 }}>{DB_LEVELS[currentDbIdx]} dB</span></div>
            <input
              type="range"
              min={0}
              max={DB_LEVELS.length - 1}
              step={1}
              value={currentDbIdx}
              onChange={e => setCurrentDbIdx(Number(e.target.value))}
              style={{ width: 250 }}
            />
            <div>
              <span style={{ fontSize: 12, color: "#888" }}>Âm lượng hệ thống:</span>
              <span style={{ fontWeight: "bold", fontSize: 16, marginLeft: 8 }}>
                {appliedCalib[FREQUENCIES[currentFreqIdx]][currentDbIdx].volume}
              </span>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: "bold", color: "#d32f2f" }}>Tần số (Hz)</div>
            <select
              style={{ fontSize: 32, fontWeight: "bold", color: "red", width: 120, textAlign: "center" }}
              value={currentFreqIdx}
              onChange={e => setCurrentFreqIdx(Number(e.target.value))}
            >
              {FREQUENCIES.map((f, idx) =>
                <option key={f} value={idx}>{f}</option>
              )}
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 24 }}>
            <label>
              <input type="radio" checked={conduction === "air"} onChange={() => setConduction("air")} /> Đường khí
            </label>
            <label>
              <input type="radio" checked={conduction === "bone"} onChange={() => setConduction("bone")} /> Đường xương
            </label>
            <label>
              <input type="checkbox" checked={soundType === "pulsed"} onChange={e => setSoundType(e.target.checked ? "pulsed" : "continuous")} /> Nhấp nháy
            </label>
            <label style={{ marginLeft: 16 }}>
              <input type="checkbox" checked={masking} onChange={e => setMasking(e.target.checked)} /> Che lấp
            </label>
          </div>
          <div>
            <label>
              <input type="radio" checked={currentEar === "right"} onChange={() => setCurrentEar("right")} /> Tai phải
            </label>
            <label style={{ marginLeft: 16 }}>
              <input type="radio" checked={currentEar === "left"} onChange={() => setCurrentEar("left")} /> Tai trái
            </label>
          </div>
        </div>
        <div style={{ marginTop: 16, display: "flex", gap: 12, alignItems: "center" }}>
          <button style={{ fontSize: 32, padding: "12px 36px", background: "#1976d2", color: "#fff", borderRadius: 8 }}
            onClick={handlePlayTone}
          >BẮT ĐẦU ĐO (Enter)</button>
          <button style={{ fontSize: 32, padding: "12px 36px", background: "#e57373", color: "#fff", borderRadius: 8 }}
            onClick={() => setCurrentDbIdx(idx => Math.max(idx - 1, 0))}
          >▼</button>
          <button style={{ fontSize: 32, padding: "12px 36px", background: "#81c784", color: "#fff", borderRadius: 8 }}
            onClick={() => setCurrentDbIdx(idx => Math.min(idx + 1, DB_LEVELS.length - 1))}
          >▲</button>
          <button style={{ marginLeft: 16 }} onClick={handleSaveResult}>Lưu kết quả đo (Ctrl+Space)</button>
        </div>
      </div>
      {/* Biểu đồ và bảng kết quả */}
      <div style={{ display: "flex", gap: 32 }}>
        <div style={{ flex: 1, background: "#fff", padding: 16, borderRadius: 8, boxShadow: "0 1px 4px #ccc" }}>
          <div style={{ fontWeight: "bold", fontSize: 18, marginBottom: 8, textAlign: "center" }}>BIỂU ĐỒ SỨC NGHE</div>
          <AudiogramChart
            results={results}
            minDb={MIN_DB}
            maxDb={MAX_DB}
            stepDb={STEP_DB}
          />
        </div>
        <div style={{ flex: 1, background: "#fff", padding: 16, borderRadius: 8, boxShadow: "0 1px 4px #ccc" }}>
          <div style={{ fontWeight: "bold", fontSize: 18, marginBottom: 8, textAlign: "center" }}>KẾT QUẢ ĐO</div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ border: "1px solid #ccc", padding: 4 }}>Tần số (Hz)</th>
                <th style={{ border: "1px solid #ccc", padding: 4 }}>Cường độ (dB)</th>
                <th style={{ border: "1px solid #ccc", padding: 4 }}>Âm lượng</th>
                <th style={{ border: "1px solid #ccc", padding: 4 }}>Tai</th>
                <th style={{ border: "1px solid #ccc", padding: 4 }}>Đường đo</th>
                <th style={{ border: "1px solid #ccc", padding: 4 }}>Masking</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, idx) => (
                <tr key={idx}>
                  <td style={{ border: "1px solid #ccc", padding: 4 }}>{r.freq}</td>
                  <td style={{ border: "1px solid #ccc", padding: 4 }}>{r.db}</td>
                  <td style={{ border: "1px solid #ccc", padding: 4 }}>{r.volume}</td>
                  <td style={{ border: "1px solid #ccc", padding: 4 }}>{r.ear === "right" ? "Tai phải" : "Tai trái"}</td>
                  <td style={{ border: "1px solid #ccc", padding: 4 }}>{r.conduction === "air" ? "Đường khí" : "Đường xương"}</td>
                  <td style={{ border: "1px solid #ccc", padding: 4 }}>{r.masking ? "Có" : "Không"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <button style={{ marginTop: 12 }} onClick={handleClearResults}>Xóa kết quả đo</button>
        </div>
      </div>
    </div>
  );
}

export default App;